/**
 * Get the full URL for a public asset path.
 *
 * For Vite apps with BASE_URL configured (e.g., white-label-host embedded on external sites),
 * this prepends the absolute base URL to ensure assets resolve correctly.
 *
 * For Next.js apps or Vite apps without BASE_URL, returns the relative path as-is.
 *
 * @param path - The asset path starting with '/' (e.g., '/partners/bank/fi/BN.svg')
 * @returns The full URL if BASE_URL is configured, otherwise the original path
 *
 * @example
 * // In white-label-host (production): BASE_URL = 'https://form.axofinance.com'
 * getPublicAssetUrl('/partners/bank/fi/BN.svg')
 * // => 'https://form.axofinance.com/partners/bank/fi/BN.svg'
 *
 * @example
 * // In Next.js sites or local dev: BASE_URL is undefined or '/'
 * getPublicAssetUrl('/partners/bank/fi/BN.svg')
 * // => '/partners/bank/fi/BN.svg'
 */
export const getPublicAssetUrl = (path: string): string => {
  // Safely check if running in Vite environment
  // Next.js doesn't have import.meta.env, so we need to guard against this
  if (
    typeof import.meta !== 'undefined' &&
    import.meta.env &&
    typeof import.meta.env.BASE_URL === 'string'
  ) {
    const baseUrl = import.meta.env.BASE_URL;

    // Only use BASE_URL if it's configured and not the default '/'
    // This ensures Next.js apps and local dev continue to use relative paths
    if (baseUrl && baseUrl !== '/') {
      // Ensure path starts with '/' and baseUrl doesn't end with '/'
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

      return `${normalizedBase}${normalizedPath}`;
    }
  }

  // Fallback to relative path (works for Next.js and default Vite)
  return path;
};
